// src/pages/DocumentAccessPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Öffentliche Seite für passwortgeschützten Dokument-Zugriff
 * 
 * Route: /dokument/:token
 * Keine Authentifizierung erforderlich
 * 
 * Flow:
 * 1. User gibt Passwort ein (letzte 4 Ziffern der IBAN)
 * 2. Validierung via /.netlify/functions/document-access
 * 3. Bei Erfolg: JSON-Response mit Download-URL, dann Navigation zum Download
 * 4. Bei Fehler: Fehlermeldung anzeigen
 *
 * Brute-Force-Schutz: Nach mehreren falschen Eingaben sperrt das Backend den
 * Link vorübergehend. Diese Seite zeigt dann die verbleibenden Versuche bzw.
 * einen mitlaufenden Countdown bis zur automatischen Freigabe an.
 */

export const DocumentAccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [passwort, setPasswort] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Brute-Force-Schutz: Restsekunden der aktuellen Sperre (null = nicht gesperrt).
  const [lockSeconds, setLockSeconds] = useState<number | null>(null);
  // Sonderfeature: passwortfreie Dokumente werden ohne Eingabe direkt geladen.
  // 'probe' = Erstprüfung beim Laden, 'form' = gewohntes Passwortformular.
  const [phase, setPhase] = useState<'probe' | 'form'>(token ? 'probe' : 'form');

  useEffect(() => {
    // Setze Page Title
    document.title = 'XTAX - Geschützter Downloadbereich';
    
    // Token-Validierung beim Laden der Seite
    if (!token) {
      setError('Ungültiger Zugriffscode. Bitte überprüfen Sie den Link.');
    }
  }, [token]);

  // Beim Laden einmal ohne Passwort anfragen. Ist das Dokument passwortfrei,
  // liefert das Backend sofort die Download-URL. Andernfalls (Passwortpflicht,
  // Sperre, Fehler) erscheint das Passwortformular; der Ablauf bei passwort-
  // geschützten Dokumenten bleibt dadurch unverändert.
  useEffect(() => {
    if (!token) return;
    let abgebrochen = false;
    (async () => {
      try {
        const response = await fetch(
          `/.netlify/functions/document-access?token=${encodeURIComponent(token)}`
        );
        const result = await response.json();
        if (abgebrochen) return;
        if (response.ok && result.valid && result.downloadUrl) {
          setSuccess(true);
          setTimeout(() => { window.location.href = result.downloadUrl; }, 800);
          return;
        }
        setPhase('form');
      } catch {
        if (!abgebrochen) setPhase('form');
      }
    })();
    return () => { abgebrochen = true; };
  }, [token]);

  // Live-Countdown der Sperre: zählt sekündlich herunter und gibt den Zugang
  // nach Ablauf automatisch wieder frei.
  useEffect(() => {
    if (lockSeconds === null) {
      return;
    }

    if (lockSeconds <= 0) {
      setLockSeconds(null);
      setError(null);
      return;
    }

    const timer = setTimeout(() => {
      setLockSeconds(lockSeconds - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [lockSeconds]);

  // Ist der Zugang aktuell gesperrt?
  const isLocked = lockSeconds !== null && lockSeconds > 0;

  // Formatiert die Restsekunden als mm:ss (z.B. 14:32).
  const formatLockTime = (seconds: number): string => {
    const minuten = Math.floor(seconds / 60);
    const sekunden = seconds % 60;
    return `${minuten}:${sekunden.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Ungültiger Zugriffscode.');
      return;
    }

    // Während einer aktiven Sperre keine weiteren Anfragen absenden.
    if (isLocked) {
      return;
    }

    if (!passwort || passwort.length !== 4) {
      setError('Bitte geben Sie die letzten 4 Ziffern Ihrer IBAN ein.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // API-Call zu Netlify Function: Zugriffscode und Passwort im JSON-Inhalt
      // einer POST-Anfrage, damit das Passwort nicht in der Adresse steht.
      const response = await fetch('/.netlify/functions/document-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, passwort })
      });

      const result = await response.json();

      if (!response.ok || !result.valid) {
        // Brute-Force-Schutz: Link gesperrt – Countdown starten.
        if (result.gesperrt && typeof result.sperreSekunden === 'number') {
          setLockSeconds(result.sperreSekunden);
          setError(null);
          setLoading(false);
          return;
        }

        // Falsches Passwort mit verbleibenden Versuchen.
        if (typeof result.verbleibendeVersuche === 'number') {
          const rest = result.verbleibendeVersuche;
          const versuchWort = rest === 1 ? 'Versuch' : 'Versuche';
          setError(`Falsches Passwort. Sie haben noch ${rest} ${versuchWort}.`);
          setLoading(false);
          return;
        }

        // Sonstiger Fehler.
        setError(result.error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        setLoading(false);
        return;
      }

      // Erfolg - Download-URL aus JSON-Response
      if (result.downloadUrl) {
        setLoading(false);
        setSuccess(true);
        
        // Kurze Verzögerung damit User den Success-Status sieht
        setTimeout(() => {
          window.location.href = result.downloadUrl;
        }, 800);
      } else {
        setError('Download-Link konnte nicht abgerufen werden. Bitte versuchen Sie es erneut.');
        setLoading(false);
      }

    } catch (err) {
      console.error('Document access error:', err);
      setError('Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      setLoading(false);
      setSuccess(false);
    }
  };

  // Button-Text basierend auf Status
  const getButtonText = () => {
    if (success) return '✓ Download erfolgreich';
    if (loading) return 'Wird überprüft...';
    return 'Gehaltsunterlagen herunterladen';
  };

  // Button-Farbe basierend auf Status
  const getButtonColor = () => {
    if (success) return '#10b981'; // Grün
    if (loading || !token || passwort.length !== 4 || isLocked) return '#9ca3af'; // Grau
    return '#3465de'; // Blau
  };

  return (
    <div style={{
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px'
    }}>
      {/* Überschrift; Logo und Fuß kommen aus dem Rahmen OeffentlicheSeite */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        textAlign: 'center',
        marginBottom: '40px',
        marginTop: '60px'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#111827',
          margin: '0'
        }}>
          Geschützter Downloadbereich
        </h1>
      </div>

      {/* Hauptinhalt */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '32px'
      }}>
        {(phase === 'probe' || success) && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <p style={{ margin: 0, fontSize: '15px', color: '#374151' }}>
              {success ? 'Ihr Download wird gestartet …' : 'Ihre Unterlagen werden vorbereitet …'}
            </p>
          </div>
        )}
        {phase === 'form' && !success && (<>
        {/* Info-Box */}
        <div style={{
          backgroundColor: '#f0f7ff',
          border: '1px solid #3465de',
          borderRadius: '6px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>🔒</span>
            <div>
              <p style={{
                margin: '0 0 8px 0',
                fontWeight: '600',
                color: '#1e40af'
              }}>
                Passwortgeschützter Downloadlink
              </p>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.5'
              }}>
                Bitte geben Sie die <strong>letzten 4 Ziffern der IBAN Ihres Gehaltskontos</strong> ein, 
                um auf Ihre Gehaltsunterlagen zugreifen zu können.
              </p>
            </div>
          </div>
        </div>

        {/* Formular */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Passwort (letzte 4 Ziffern der IBAN Ihres Gehaltskontos)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={passwort}
              onChange={(e) => {
                // Nur Zahlen erlauben
                const value = e.target.value.replace(/[^0-9]/g, '');
                setPasswort(value);
              }}
              placeholder="z.B. 1234"
              disabled={loading || success || !token || isLocked}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                outline: 'none',
                transition: 'border-color 0.2s',
                fontFamily: 'monospace',
                letterSpacing: '4px',
                textAlign: 'center',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3465de';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
              }}
            />
          </div>

          {/* Sperr-Anzeige (Brute-Force-Schutz) mit Live-Countdown */}
          {isLocked && (
            <div style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>🔒</span>
                <div>
                  <p style={{
                    margin: '0 0 4px 0',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#92400e'
                  }}>
                    Zugang vorübergehend gesperrt
                  </p>
                  <p style={{
                    margin: '0 0 6px 0',
                    fontSize: '14px',
                    color: '#92400e',
                    lineHeight: '1.5'
                  }}>
                    Aus Sicherheitsgründen ist dieser Zugang noch{' '}
                    <strong>{formatLockTime(lockSeconds ?? 0)}</strong> Minuten gesperrt.
                  </p>
                  <p style={{
                    margin: '0',
                    fontSize: '13px',
                    color: '#92400e',
                    lineHeight: '1.5'
                  }}>
                    Bei Fragen wenden Sie sich bitte an{' '}
                    <a
                      href="mailto:lohn@xtax.de"
                      style={{ color: '#92400e', fontWeight: '600', textDecoration: 'none' }}
                    >
                      lohn@xtax.de
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Fehleranzeige */}
          {!isLocked && error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <p style={{
                  margin: '0',
                  fontSize: '14px',
                  color: '#991b1b'
                }}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || success || !token || passwort.length !== 4 || isLocked}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: getButtonColor(),
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || success || !token || passwort.length !== 4 || isLocked) ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!loading && !success && token && passwort.length === 4 && !isLocked) {
                e.currentTarget.style.backgroundColor = '#2952b3';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && !success && token && passwort.length === 4 && !isLocked) {
                e.currentTarget.style.backgroundColor = '#3465de';
              }
            }}
          >
            {getButtonText()}
          </button>
        </form>

        {/* Hinweis */}
        <div style={{
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{
            margin: '0',
            fontSize: '13px',
            color: '#6b7280',
            lineHeight: '1.5'
          }}>
            <strong>Hinweis:</strong> Ihre Gehaltsunterlagen werden aus Datenschutz- und Datensicherheitserwägungen durch einen passwortgeschützten Downloadlink geschützt. 
            Falls Sie Probleme beim Zugriff haben, wenden Sie sich bitte an{' '}
            <a 
              href="mailto:lohn@xtax.de" 
              style={{ color: '#3465de', textDecoration: 'none' }}
            >
              lohn@xtax.de
            </a>
          </p>
        </div>
        </>)}
      </div>

    </div>
  );
};
